import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { Check, Package, Plus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/loading-button'
import { cn, formatMoney } from '@/lib/utils'
import { useQuoteBuilderApi } from '../api/client'
import type { QuoteLine } from '../api/types'
import { AiData } from '../ui/ai-data'
import { PageState } from '../ui/page'
import { ProductImage } from '../catalog/product-image'
import {
  accessoryGroups,
  configurationChoiceGroups,
  configurationOptionName,
  configurationQueryOptions,
  defaultChoices,
  versionGroups,
  type ConfigurationGroup,
  type ProductSummary,
} from '../catalog/queries'
import { DrawerFooter, EditorDrawer } from './drawer'

const unavailableReasons: Record<string, string> = {
  not_in_live_catalog: 'Not in the live catalog',
}

/** One configuration group: exactly one choice, as the catalog lists them. */
function ChoiceGroup({
  group,
  selected,
  onChoose,
  name,
}: {
  group: ConfigurationGroup
  selected: string | undefined
  onChoose: (optionId: string) => void
  name: string
}) {
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1.5 text-sm font-medium">
        {group.name}
        {group.required ? <span className="sr-only"> (required)</span> : null}
      </legend>
      {group.options.map((option) => {
        const checked = selected === option.id
        const delta = Number(option.price_delta ?? 0)
        return (
          <AiData asChild key={option.id} label="AI-generated configuration data">
            <label
              className={cn(
                'flex min-h-11 cursor-pointer items-center gap-3 rounded-lg border py-2.5 pl-3 pr-8 text-sm transition-colors',
                checked ? 'border-foreground/70 ring-1 ring-foreground/40' : 'hover:border-emerald-300',
                !option.available && 'cursor-not-allowed opacity-60',
              )}
            >
              <input
                checked={checked}
                className="size-4 accent-primary"
                disabled={!option.available}
                name={name}
                onChange={() => onChoose(option.id)}
                type="radio"
              />
              <span className="min-w-0 flex-1">
                <span className="block font-medium [overflow-wrap:anywhere]">{configurationOptionName(option)}</span>
                {!option.available ? (
                  <span className="block text-xs text-muted-foreground">
                    {unavailableReasons[option.unavailable_reason ?? ''] ?? 'Unavailable'}
                  </span>
                ) : null}
              </span>
              {delta ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {delta > 0 ? '+' : ''}
                  {formatMoney(delta)}
                </span>
              ) : null}
            </label>
          </AiData>
        )
      })}
    </fieldset>
  )
}

/** A choice whose values are other SKUs: picking one swaps the line's product (USA-1392). */
function VersionGroup({
  group,
  onSwitch,
}: {
  group: ConfigurationGroup
  onSwitch: (product: ProductSummary) => void
}) {
  return (
    <fieldset className="grid gap-2">
      <legend className="mb-1.5 flex flex-wrap items-baseline gap-x-2 text-sm font-medium">
        {group.name}
        <span className="text-xs font-normal text-muted-foreground">Changes the product</span>
      </legend>
      <div className="flex flex-wrap gap-2">
        {group.options.map((option) => {
          const current = option.is_default
          const delta = Number(option.price_delta ?? 0)
          const product = option.product
          return (
            <button
              aria-pressed={current}
              className={cn(
                'inline-flex min-h-11 items-center gap-2 rounded-full border px-4 py-2 text-sm transition-colors',
                current
                  ? 'cursor-default border-foreground/70 font-medium ring-1 ring-foreground/40'
                  : 'hover:border-emerald-300',
                !option.available && 'cursor-not-allowed opacity-60',
              )}
              disabled={!current && (!option.available || !product)}
              key={option.id}
              onClick={() => {
                if (!current && product) onSwitch(product)
              }}
              title={
                product
                  ? `${product.manufacturer} ${product.model}${option.available ? '' : ' (not in the live catalog)'}`
                  : undefined
              }
              type="button"
            >
              {configurationOptionName(option)}
              {!current && delta ? (
                <span className="text-xs tabular-nums text-muted-foreground">
                  {delta > 0 ? '+' : ''}
                  {formatMoney(delta)}
                </span>
              ) : null}
            </button>
          )
        })}
      </div>
    </fieldset>
  )
}

function OptionsState({
  loading,
  failed,
  empty,
  onRetry,
}: {
  loading: boolean
  failed: boolean
  empty: boolean
  onRetry: () => void
}) {
  if (loading) return <PageState compact kind="loading" title="Loading options" />
  if (failed)
    return (
      <PageState
        action={
          <Button onClick={onRetry} size="sm" variant="outline">
            Retry options
          </Button>
        }
        compact
        kind="error"
        title="Options could not be loaded"
      />
    )
  if (empty) return <p className="rounded-lg bg-muted p-4 text-sm text-muted-foreground">No configuration options.</p>
  return null
}

/** Choose a new product's configuration before it is added (demo `CatalogConfigurationDrawer`).
 * Import review opens it to confirm a row's options, starting from the import's own choices. */
export function CatalogConfigurationDrawer({
  product,
  onCancel,
  onAdd,
  title = 'Add to project',
  actionLabel,
  initialChoices = {},
}: {
  product: Pick<ProductSummary, 'id' | 'manufacturer' | 'model' | 'title'>
  onCancel: () => void
  onAdd: (configuration: Record<string, string>) => void
  title?: string
  actionLabel?: string
  initialChoices?: Record<string, string>
}) {
  const api = useQuoteBuilderApi()
  const configuration = useQuery(configurationQueryOptions(api, product.id))
  const [choices, setChoices] = useState<Record<string, string>>(initialChoices)
  const groups = configurationChoiceGroups(configuration.data)
  const selected = { ...defaultChoices(groups), ...choices }
  const missing = groups.some((group) => group.required && !selected[group.name])
  return (
    <EditorDrawer onOpenChange={(open) => !open && onCancel()} open title={title}>
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <h3 className="text-base font-semibold">
          {product.manufacturer} {product.model}
        </h3>
        <p className="mb-5 mt-1 text-sm text-muted-foreground">{product.title}</p>
        <OptionsState
          empty={configuration.isSuccess && !groups.length}
          failed={configuration.isError}
          loading={configuration.isLoading}
          onRetry={() => void configuration.refetch()}
        />
        <div className="grid gap-5">
          {groups.map((group) => (
            <ChoiceGroup
              group={group}
              key={group.id}
              name={`catalog-${group.id}`}
              onChoose={(id) => setChoices((current) => ({ ...current, [group.name]: id }))}
              selected={selected[group.name]}
            />
          ))}
        </div>
      </div>
      <DrawerFooter>
        <Button onClick={onCancel} variant="outline">
          Cancel
        </Button>
        <Button disabled={!configuration.isSuccess || missing} onClick={() => onAdd(selected)}>
          {actionLabel ? <Check /> : <Plus />}
          {actionLabel ?? 'Add to project'}
        </Button>
      </DrawerFooter>
    </EditorDrawer>
  )
}

/** A line's options and accessories (demo `ConfigurationDrawer`). */
export function ConfigurationDrawer({
  line,
  busy,
  onOpenChange,
  onSave,
  onAddAccessory,
  onSwitchVersion,
}: {
  line: QuoteLine | null
  busy: boolean
  onOpenChange: (open: boolean) => void
  onSave: (line: QuoteLine, configuration: Record<string, string>) => void
  onAddAccessory: (line: QuoteLine, product: ProductSummary) => void
  /** Replace the line's product with a sibling SKU; its other choices restart from the new product's defaults. */
  onSwitchVersion: (line: QuoteLine, product: ProductSummary) => void
}) {
  const api = useQuoteBuilderApi()
  const configuration = useQuery({
    ...configurationQueryOptions(api, line?.product_id ?? 0),
    enabled: Boolean(line?.product_id),
  })
  const [choices, setChoices] = useState<Record<string, string>>({})
  const [lineId, setLineId] = useState(line?.id)
  if (line?.id !== lineId) {
    setLineId(line?.id)
    setChoices({})
  }
  const versions = versionGroups(configuration.data)
  const groups = configurationChoiceGroups(configuration.data)
  const accessories = accessoryGroups(configuration.data)
  const selected = {
    ...defaultChoices(groups, line?.configuration ?? {}, line?.configuration_display ?? {}),
    ...choices,
  }

  return (
    <EditorDrawer onOpenChange={onOpenChange} open={Boolean(line)} title="Options & accessories">
      <div className="min-h-0 flex-1 overflow-y-auto p-5">
        <p className="mb-5 text-base font-semibold">
          {line?.model ? `${line.manufacturer ?? ''} ${line.model}`.trim() : line?.description}
        </p>
        <OptionsState
          empty={configuration.isSuccess && !configuration.data.groups.length && !versions.length}
          failed={configuration.isError}
          loading={configuration.isLoading}
          onRetry={() => void configuration.refetch()}
        />
        <div className="grid gap-5">
          {versions.map((group) => (
            <VersionGroup
              group={group}
              key={group.id}
              onSwitch={(product) => {
                if (!line) return
                onOpenChange(false)
                onSwitchVersion(line, product)
              }}
            />
          ))}
          {groups.map((group) => (
            <ChoiceGroup
              group={group}
              key={group.id}
              name={`line-${group.id}`}
              onChoose={(id) => setChoices((current) => ({ ...current, [group.name]: id }))}
              selected={selected[group.name]}
            />
          ))}
        </div>
        {accessories.map((group) => (
          <section className="mt-6 border-t pt-4" key={group.id}>
            <h3 className="mb-3 text-sm font-medium">{group.name}</h3>
            <div className="grid gap-3">
              {group.options.map((option) => (
                <AiData
                  className="flex items-start gap-3 py-2 pl-2 pr-7"
                  key={option.id}
                  label="AI-generated accessory data"
                >
                  {option.product?.primary_asset?.url ? (
                    <ProductImage
                      alt=""
                      className="size-12 shrink-0 rounded-md border"
                      url={option.product.primary_asset.url}
                    />
                  ) : (
                    <span
                      aria-hidden="true"
                      className="grid size-12 shrink-0 place-items-center rounded-md border bg-muted text-muted-foreground"
                    >
                      <Package className="size-5" />
                    </span>
                  )}
                  <div className="min-w-0 flex-1">
                    <p className="break-words text-sm font-medium">
                      {option.product?.model ?? configurationOptionName(option)}
                    </p>
                    <p className="mt-0.5 break-words text-xs text-muted-foreground">
                      {configurationOptionName(option)}
                    </p>
                  </div>
                  <Button
                    disabled={!option.available || !option.product}
                    onClick={() => {
                      if (line && option.product) onAddAccessory(line, option.product)
                      onOpenChange(false)
                    }}
                    size="sm"
                    variant="outline"
                  >
                    Add
                  </Button>
                </AiData>
              ))}
            </div>
          </section>
        ))}
      </div>
      <DrawerFooter>
        <LoadingButton
          disabled={!groups.length}
          loading={busy}
          loadingText="Applying..."
          onClick={() => {
            if (!line) return
            onOpenChange(false)
            onSave(line, selected)
          }}
        >
          <Check />
          Apply configuration
        </LoadingButton>
      </DrawerFooter>
    </EditorDrawer>
  )
}
